import type { Unsubscribe } from '../core/services';

export interface Store<T extends object> {
  get(): T;
  /** Shallow-merges and notifies only when a field actually changed. */
  set(patch: Partial<T>): void;
  subscribe(listener: (state: T) => void): Unsubscribe;
}

export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<(state: T) => void>();
  return {
    get: () => state,
    set(patch) {
      let changed = false;
      for (const key of Object.keys(patch) as Array<keyof T>) {
        if (!Object.is(state[key], patch[key])) changed = true;
      }
      if (!changed) return;
      state = { ...state, ...patch };
      for (const listener of [...listeners]) listener(state);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** 128 random bits as hex. `crypto.getRandomValues` also works outside secure contexts. */
export function randomId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** 128 random bits as 22 base64url characters: the id format the signalling server checks for. */
export function randomKey22(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
